"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown, Pencil } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useEstimator } from "../../_context/EstimatorContext";
import { useElementWidth } from "../../_hooks/useElementWidth";
import {
  DATES_TRIGGER_ID,
  ESTIMATE_LABEL_PX,
  WHO_TRIGGER_ID,
  TRIP_BAR_ROOMY_PX,
  TRIP_BAR_STACK_PX,
} from "../../_logic/constants";
import { missingInputs } from "../../_logic/estimate";
import type { WhoDraft } from "../../_logic/trip-drafts";
import { summarizeTrip } from "../../_logic/trip-summary";
import { homeCities } from "../../_logic/workspace";
import { RangeCalendar } from "../ui/RangeCalendar";
import { TripOptionsEditor } from "./TripOptionsEditor";
import { TripSegment } from "./TripSegment";
import { WhereEditor } from "./WhereEditor";
import { WhoEditor } from "./WhoEditor";
import { DURATION, EASE_OUT } from "../../_logic/motion";

type Segment = "who" | "where" | "when" | "options";

const FIRST_MISSING: Record<ReturnType<typeof missingInputs>[number], Segment> = {
  origins: "who",
  destinations: "where",
  dates: "when",
};

/**
 * The trip as one segmented bar, in the style of the hotel search bar: Who, Where and When, a
 * trailing cabin and currency control, and Estimate. A white pill slides under the segment that
 * is open or focused; each segment opens a popover with its full editor. Narrow columns stack it
 * into a compact card.
 * @param props.autoFocus - Focus Who on mount, for a blank estimate.
 * @returns The bar.
 * @throws Never.
 */
export const TripBar = ({ autoFocus = false }: { autoFocus?: boolean }) => {
  // Hooks
  const { workspace, estimate, edit, flights } = useEstimator();
  const track = useRef<HTMLDivElement>(null);
  const triggers = useRef(new Map<Segment, HTMLButtonElement | null>());

  // States
  const [open, setOpen] = useState<Segment | null>(null);
  const [focused, setFocused] = useState<Segment | null>(null);
  const [pill, setPill] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  /** The planner opened the stacked editor from the one-line summary. */
  const [expanded, setExpanded] = useState(false);
  // Rows started but not picked yet, kept here so closing a popover keeps what was typed.
  const [whoDrafts, setWhoDrafts] = useState<WhoDraft[]>([]);
  const [whereDraft, setWhereDraft] = useState<string | null>(null);
  const { ref: frame, width: frameWidth } = useElementWidth<HTMLDivElement>();
  const reduceMotion = useReducedMotion();

  // Derived
  const summary = summarizeTrip(workspace);
  const touched = new Set(workspace.chatTouched);
  const active = open ?? focused;
  // Stacked into a card (narrow column): popovers take the card's full width.
  const stackedWidth = trackWidth > 0 && trackWidth < TRIP_BAR_STACK_PX ? trackWidth : null;
  const stacked = frameWidth > 0 && frameWidth < TRIP_BAR_STACK_PX;
  const complete = missingInputs(workspace).length === 0;
  // A narrow column with the trip filled in shows one summary line; Edit opens the stacked editor.
  const collapsed = stacked && complete && !expanded;
  const summaryLine = [
    [summary.who, summary.where].filter(Boolean).join(" → "),
    summary.when,
    summary.nights,
    summary.options,
  ]
    .filter(Boolean)
    .join(" · ");
  const largestOrigin = homeCities(workspace.origins)[0] ?? null;
  const segment = (name: Segment) => ({
    ref: (node: HTMLButtonElement | null) => {
      triggers.current.set(name, node);
    },
    state: {
      open: open === name,
      active: active === name,
      onOpenChange: (next: boolean) => setOpen((current) => (next ? name : current === name ? null : current)),
      onFocusChange: (next: boolean) => setFocused((current) => (next ? name : current === name ? null : current)),
    },
  });

  // Handlers
  const handleEstimate = () => {
    // The first empty segment in the bar's own order, so an empty bar opens Who, not When.
    const missing = missingInputs(workspace);
    const first = (["origins", "destinations", "dates"] as const).find((input) => missing.includes(input));
    if (first) {
      setOpen(FIRST_MISSING[first]);
      return;
    }
    // Complete: re-price what failed, then take the planner to the results (focus too, so it's heard).
    if (flights.status === "error") flights.refresh();
    const results = document.querySelector<HTMLElement>('section[aria-label="Comparison"]');
    results?.scrollIntoView({ behavior: "smooth", block: "start" });
    results?.focus({ preventScroll: true });
  };

  // Effects
  useEffect(() => {
    if (autoFocus) triggers.current.get("who")?.focus({ preventScroll: true });
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const measure = () => {
      setTrackWidth(track.current?.offsetWidth ?? 0);
      const target = active ? triggers.current.get(active) : null;
      setPill(
        target
          ? { top: target.offsetTop, left: target.offsetLeft, width: target.offsetWidth, height: target.offsetHeight }
          : null
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (track.current) observer.observe(track.current);
    return () => observer.disconnect();
  }, [active, summary.who, summary.where, summary.when, summary.nights, summary.options]);

  // Render
  if (collapsed) {
    return (
      <div ref={frame} className="@container">
        <motion.button
          type="button"
          layout={!reduceMotion}
          onClick={() => setExpanded(true)}
          aria-label={`Edit trip: ${summaryLine}`}
          className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-left shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <span className="min-w-0 flex-1 text-sm font-medium text-gray-900">{summaryLine}</span>
          <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-indigo-700">
            <Pencil aria-hidden className="size-3.5" />
            Edit
          </span>
        </motion.button>
      </div>
    );
  }

  return (
    <div ref={frame} className="@container">
      <motion.div
        layout={!reduceMotion}
        initial={stacked && !reduceMotion ? { opacity: 0, y: -4 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT }}
        ref={track}
        role="group"
        aria-label="Trip"
        data-tour="estimator-trip-bar"
        className="relative flex flex-col items-stretch gap-0.5 rounded-3xl border border-gray-200 bg-gray-50 p-1 shadow-sm transition-shadow hover:border-gray-300 hover:shadow-md @[520px]:flex-row @[520px]:gap-0 @[520px]:rounded-full @[520px]:p-0"
      >
        <div
          aria-hidden
          className={`pointer-events-none absolute left-0 top-0 rounded-full bg-white shadow-md transition-[transform,width,height,opacity] duration-220 motion-reduce:transition-none ease-[cubic-bezier(0.22,1,0.36,1)] ${pill ? "opacity-100" : "opacity-0"}`}
          // Moves by transform; only its size changes between segments of different widths.
          style={
            pill
              ? { transform: `translate(${pill.left}px, ${pill.top}px)`, width: pill.width, height: pill.height }
              : undefined
          }
        />
        <TripSegment
          {...segment("who")}
          content={{
            id: WHO_TRIGGER_ID,
            title: "Who",
            value: summary.who,
            placeholder: "Add travelers",
            byChat: workspace.chatTouched.some((key) => key.startsWith("origin:")),
          }}
          panel={{ className: "w-[480px]", width: stackedWidth }}
          className="flex-[1.4]"
        >
          <WhoEditor focusCity={largestOrigin} drafts={whoDrafts} setDrafts={setWhoDrafts} />
        </TripSegment>
        <TripSegment
          {...segment("where")}
          content={{
            title: "Where",
            value: summary.where,
            placeholder: "Add cities",
            byChat: workspace.chatTouched.some((key) => key.startsWith("destination:")),
          }}
          panel={{ className: "w-[400px]", width: stackedWidth }}
        >
          <WhereEditor
            onAsk={() => setOpen(null)}
            focusCity={workspace.destinations[0] ?? null}
            draft={whereDraft}
            setDraft={setWhereDraft}
          />
        </TripSegment>
        <TripSegment
          {...segment("when")}
          content={{
            id: DATES_TRIGGER_ID,
            title: "When",
            titleDetail: summary.nights,
            value: summary.when,
            placeholder: "Add dates",
            byChat: touched.has("dates"),
          }}
          panel={{ className: "w-auto" }}
        >
          <RangeCalendar
            value={workspace.dates}
            onChange={(dates) => {
              edit({ type: "setDates", dates }, "user");
              setOpen(null);
            }}
          />
          {workspace.dates && (
            <p className="text-xs font-medium text-gray-600">
              {estimate.nights} {estimate.nights === 1 ? "night" : "nights"}. Tap a new first night to change.
            </p>
          )}
        </TripSegment>
        <TripSegment
          {...segment("options")}
          content={{
            value:
              frameWidth > 0 && frameWidth < TRIP_BAR_ROOMY_PX + ESTIMATE_LABEL_PX
                ? summary.optionsShort
                : summary.options,
            placeholder: "",
            byChat: touched.has("cabin") || touched.has("airlines"),
          }}
          panel={{ className: "w-[320px]" }}
          className="flex-none @[520px]:px-3 @[760px]:px-4"
          trailing={<ChevronDown aria-hidden className="size-4 shrink-0 text-gray-500" />}
        >
          <TripOptionsEditor onPicked={() => setOpen(null)} />
        </TripSegment>
        {stacked && complete ? (
          // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="relative z-10 m-1 inline-flex h-10 shrink-0 items-center justify-center self-stretch rounded-full px-5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            Done
          </button>
        ) : (
          // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
          <button
            type="button"
            onClick={handleEstimate}
            className="relative z-10 m-1 inline-flex h-11 shrink-0 items-center justify-center gap-2 self-stretch rounded-full bg-indigo-600 px-5 text-sm font-medium text-white transition-colors hover:bg-indigo-700 active:bg-indigo-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-2 @[520px]:m-2 @[520px]:h-12 @[520px]:self-center"
          >
            Estimate
            <ArrowRight className="size-4" strokeWidth={2.5} />
          </button>
        )}
      </motion.div>
    </div>
  );
};
